// React
import React, { Fragment } from "react";

import { isInternalHref } from "./blocks/blockUtils";

// Light markdown of the assistant replies, rendered as React elements (no HTML string, no
// dangerouslySetInnerHTML): paragraphs, line breaks, "-"/"*" and "1." lists, **bold**, *italic*,
// `code`, ``` blocks, [links](url), "#" titles shown as bold lines, "> " quotes and "---".
// Links: "/..." navigate in the app, http(s) and mailto open outside, anything else is text.

const INLINE_RE =
  /(`[^`\n]+`)|\[([^\]\n]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)|\*\*([^*\n]+?)\*\*|__([^_\n]+?)__|\*([^*\s][^*\n]*?)\*/g;

const isExternalHref = (href) => /^(https?:\/\/|mailto:)/i.test(href);
// Host of an http(s) link, address of a mailto
const externalDestination = (href) => {
  if (/^mailto:/i.test(href)) return href.slice(7).split("?")[0];
  try {
    return new URL(href).host;
  } catch {
    return href;
  }
};

function AiLink({ href, children, onNavigate }) {
  if (isInternalHref(href)) {
    return (
      <a
        href={href}
        className="font-medium text-primary-strong underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
        onClick={(event) => {
          // ⌘/Ctrl/Shift click and middle click keep the browser behaviour (new tab, window)
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) {
            return;
          }
          event.preventDefault();
          onNavigate?.(href);
        }}
      >
        {children}
      </a>
    );
  }
  if (isExternalHref(href)) {
    // The text is chosen by the model (it reads CRM texts): the destination is always shown
    const destination = externalDestination(href);
    return (
      <>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={href}
          className="font-medium text-primary-strong underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
        >
          {children}
        </a>
        {destination && <span className="text-gray-500"> ({destination})</span>}
      </>
    );
  }
  return <>{children}</>;
}

const renderInline = (text, onNavigate, keyPrefix = "i") => {
  const nodes = [];
  let last = 0;
  let index = 0;
  const re = new RegExp(INLINE_RE.source, "g");
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const key = `${keyPrefix}-${index++}`;
    const [, code, linkText, linkHref, bold, boldAlt, italic] = match;
    if (code) {
      nodes.push(
        <code
          key={key}
          className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-gray-800"
        >
          {code.slice(1, -1)}
        </code>
      );
    } else if (linkText) {
      nodes.push(
        <AiLink key={key} href={linkHref} onNavigate={onNavigate}>
          {renderInline(linkText, onNavigate, key)}
        </AiLink>
      );
    } else if (bold || boldAlt) {
      nodes.push(
        <strong key={key} className="font-semibold text-gray-900">
          {renderInline(bold || boldAlt, onNavigate, key)}
        </strong>
      );
    } else if (italic) {
      nodes.push(<em key={key}>{renderInline(italic, onNavigate, key)}</em>);
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

// Lines joined with <br>: the replies use single line breaks on purpose
const renderLines = (lines, onNavigate, keyPrefix) =>
  lines.map((line, index) => (
    <Fragment key={`${keyPrefix}-${index}`}>
      {index > 0 && <br />}
      {renderInline(line, onNavigate, `${keyPrefix}-${index}`)}
    </Fragment>
  ));

const UL_RE = /^(\s*)[-*+]\s+(.*)$/;
const OL_RE = /^(\s*)(\d+)[.)]\s+(.*)$/;
const HEADING_RE = /^#{1,6}\s+(.*)$/;
const QUOTE_RE = /^>\s?(.*)$/;
const HR_RE = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;
const FENCE_RE = /^\s*```/;

// Groups the lines in blocks: { type: "p"|"ul"|"ol"|"h"|"quote"|"code"|"hr", lines|items }
const parseBlocks = (source) => {
  const lines = String(source ?? "")
    .replace(/\r\n?/g, "\n")
    .split("\n");
  const blocks = [];
  let current = null;
  const flush = () => {
    if (current) blocks.push(current);
    current = null;
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (FENCE_RE.test(line)) {
      flush();
      const code = [];
      i += 1;
      while (i < lines.length && !FENCE_RE.test(lines[i])) {
        code.push(lines[i]);
        i += 1;
      }
      blocks.push({ type: "code", lines: code });
      continue;
    }
    if (!line.trim()) {
      flush();
      continue;
    }
    if (HR_RE.test(line)) {
      flush();
      blocks.push({ type: "hr" });
      continue;
    }
    const heading = line.match(HEADING_RE);
    if (heading) {
      flush();
      blocks.push({ type: "h", lines: [heading[1]] });
      continue;
    }
    const ul = line.match(UL_RE);
    const ol = !ul && line.match(OL_RE);
    if (ul || ol) {
      const type = ul ? "ul" : "ol";
      if (current?.type !== type) {
        flush();
        current = { type, items: [], start: ol ? Number(ol[2]) : 1 };
      }
      const indent = (ul ? ul[1] : ol[1]).replace(/\t/g, "  ").length;
      current.items.push({ text: ul ? ul[2] : ol[3], nested: indent >= 2 });
      continue;
    }
    const quote = line.match(QUOTE_RE);
    if (quote) {
      if (current?.type !== "quote") {
        flush();
        current = { type: "quote", lines: [] };
      }
      current.lines.push(quote[1]);
      continue;
    }
    // A plain line right after a list item continues that item
    if ((current?.type === "ul" || current?.type === "ol") && /^\s+\S/.test(line)) {
      const item = current.items[current.items.length - 1];
      item.text = `${item.text} ${line.trim()}`;
      continue;
    }
    if (current?.type !== "p") {
      flush();
      current = { type: "p", lines: [] };
    }
    current.lines.push(line);
  }
  flush();
  return blocks;
};

/**
 * @param {{ text: string, onNavigate?: (path: string) => void, className?: string }} props
 */
export default function AiMarkdown({ text, onNavigate, className = "" }) {
  const blocks = parseBlocks(text);
  return (
    <div className={`space-y-2 break-words ${className}`}>
      {blocks.map((block, index) => {
        const key = `b${index}`;
        switch (block.type) {
          case "h":
            return (
              <p key={key} className="font-semibold text-gray-900">
                {renderInline(block.lines[0], onNavigate, key)}
              </p>
            );
          case "ul":
          case "ol": {
            const List = block.type === "ul" ? "ul" : "ol";
            return (
              <List
                key={key}
                start={block.type === "ol" && block.start !== 1 ? block.start : undefined}
                className={`space-y-1 pl-5 ${block.type === "ul" ? "list-disc" : "list-decimal"} marker:text-gray-400`}
              >
                {block.items.map((item, itemIndex) => (
                  <li key={`${key}-${itemIndex}`} className={item.nested ? "ml-4" : undefined}>
                    {renderInline(item.text, onNavigate, `${key}-${itemIndex}`)}
                  </li>
                ))}
              </List>
            );
          }
          case "quote":
            return (
              <blockquote key={key} className="border-l-2 border-gray-200 pl-3 text-gray-600">
                {renderLines(block.lines, onNavigate, key)}
              </blockquote>
            );
          case "code":
            return (
              <pre
                key={key}
                className="overflow-x-auto rounded-lg bg-gray-50 p-3 font-mono text-[12px] leading-relaxed text-gray-800"
              >
                <code>{block.lines.join("\n")}</code>
              </pre>
            );
          case "hr":
            return <hr key={key} className="border-gray-200" />;
          default:
            return <p key={key}>{renderLines(block.lines, onNavigate, key)}</p>;
        }
      })}
    </div>
  );
}
